<template>
  <div class="ServicesSettings">
    <v-row no-gutters>
      <v-col>
        <v-tabs :value="tab" color="secondary" grow show-arrows>
          <v-tab
            v-for="(service, index) in query.services"
            :key="index"
            @click="changeTab(index)"
            class="ServicesSettings-tab text-none"
          >
            <v-icon color="secondary" left>{{ service.icon }}</v-icon>
            <div v-if="service.serviceType === 'custom'">
              {{ service.name }}
            </div>
            <div v-else>
              {{ $t(`services.${service.serviceType}`) }}
            </div>
          </v-tab>
          <v-tab>
            <v-btn @click="clickAddService" class="ServicesSettings-addService" color="transparent" depressed>
              <v-icon color="grey" left>mdi-plus-circle-outline</v-icon>
              {{ $t('servicesSettings.addServiceButton') }}
            </v-btn>
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>
    <v-tabs-items v-if="!displayAddService" v-model="tab" class="transparent">
      <v-tab-item v-for="(service, index) in query.services" :key="index" class="transparent">
        <ValidationObserver v-slot="{ handleSubmit }">
          <div class="px-6 pt-10 pb-6 my-8 white">
            <div class="d-flex justify-center">
              <ServiceSettings v-if="service" v-model="query.services[index]" @edit="$set(isModified, index, true)" />
            </div>
            <v-row justify="space-between" align="end">
              <v-col>
                <v-switch
                  :value="!service.enabled"
                  @change="disableService"
                  color="secondary"
                  data-e2e="disableServiceButton"
                  :label="$t('servicesSettings.disableServiceButton')"
                  hide-details
                />
              </v-col>
              <v-spacer />
              <v-col class="text-right">
                <v-btn
                  @click="handleSubmit(updateService)"
                  class="d-block mt-7 ml-auto mr-lg-12"
                  color="primary"
                  depressed
                  :disabled="!isModified[index]"
                  :loading="loading"
                  >{{ $t('servicesSettings.submitButton') }}</v-btn
                >
                <v-alert
                  v-if="saved"
                  class="d-inline-block mt-3"
                  dense
                  text
                  transition="slide-y-transition"
                  type="success"
                  :value="saved"
                  >{{ $t('shop.savedSuccess') }}</v-alert
                >
              </v-col>
            </v-row>
          </div>

          <v-alert v-if="!service.serviceUuid" type="info" text>{{
            $t('servicesSettings.messageHideExceptionalEvents')
          }}</v-alert>
          <div v-else class="px-6 pt-10 pb-6 my-8 white">
            <ExceptionalClosureService
              v-if="!$apollo.queries.serviceExceptionalEvents.loading"
              v-model="exceptions[index]"
              @change="isExceptionalModified[index] = true"
              @clickCanceled="openCanceled"
              @removeExceptionalEvent="removeExceptionalEventLine"
            />
            <div v-else class="d-flex justify-center align-center">
              <Loader />
            </div>
            <div>
              <v-btn
                @click="createExceptionalEvents"
                class="d-block mt-7 ml-auto mr-lg-12"
                color="primary"
                depressed
                :disabled="!isExceptionalModified[index]"
                >{{ $t('servicesSettings.submitButton') }}</v-btn
              >
            </div>
          </div>
        </ValidationObserver>
      </v-tab-item>
    </v-tabs-items>
    <div v-else>
      <v-card class="my-8">
        <SelectService :selectedServices="query.services.map(el => el.serviceType)" @select="selectService" />
      </v-card>
      <CustomServiceCreation
        :shop="this.currentShop.shopUuid"
        v-if="createACustomService"
        @createService="selectService"
      />
    </div>

    <v-alert v-if="error" class="mt-3" type="error">{{ error }}</v-alert>
    <AppointmentChangesModal
      v-if="displayCancelAppointments"
      @close="displayCancelAppointments = !displayCancelAppointments"
      @cancelAppointment="cancelAppointment"
      :appointments="cancelAppointments"
      :show="displayCancelAppointments"
    />
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { mapGetters } from 'vuex'
import addDays from 'date-fns/addDays'
import { ValidationObserver } from 'vee-validate'

import ExceptionalClosureService from '@/components/settings/ExceptionalClosureService'
import SelectService from '@/components/settings/SelectService'
import ServiceSettings from '@/components/settings/ServiceSettings'
import AppointmentChangesModal from '@/components/modals/AppointmentChangesModal'
import Loader from '@/components/lib/Loader'
import { convertMinutesToHours, convertHoursToMinutes, formatHourToFloat } from '@/services/time'
import { setToBeginDay } from '@/services/date'
import CustomServiceCreation from '../components/settings/CustomServiceCreation.vue'

const GET_EXCEPTIONAL = gql`
  query serviceExceptionalEvents($limit: Int!, $offset: Int!, $serviceUuid: GUID!) {
    serviceExceptionalEvents(limit: $limit, offset: $offset, serviceUuid: $serviceUuid) {
      total
      exceptionalEvents {
        beginDateTime
        endDateTime
        eventType
        exceptionalEventUuid
        serviceUuid
        toCancelAppointments {
          appointmentId
          beginDateTime
          customerFullName
          customerPhoneNumber
          endDateTime
          service {
            serviceType
          }
        }
      }
    }
  }
`

const CREATE_SERVICE_EXCEPTIONAL_EVENTS = gql`
  mutation createServiceExceptionalEvents(
    $serviceUuid: GUID!
    $serviceExceptionalEvents: [ServiceExceptionalEventInput!]!
  ) {
    createServiceExceptionalEvents(serviceUuid: $serviceUuid, serviceExceptionalEvents: $serviceExceptionalEvents) {
      beginDateTime
      endDateTime
      eventType
      exceptionalEventUuid
      serviceUuid
      toCancelAppointments {
        appointmentId
        beginDateTime
        endDateTime
        customerFullName
        customerPhoneNumber
        service {
          serviceType
        }
      }
    }
  }
`

const REMOVE_EXCEPTIONAL_EVENT = gql`
  mutation removeServiceExceptionalEvent($serviceUuid: GUID!, $serviceExceptionalEventUuid: GUID!) {
    removeServiceExceptionalEvent(serviceUuid: $serviceUuid, serviceExceptionalEventUuid: $serviceExceptionalEventUuid)
  }
`

const CANCEL_APPOINTMENT = gql`
  mutation cancelAppointment($appointmentId: CustomId!) {
    cancelAppointment(appointmentId: $appointmentId) {
      appointmentId
    }
  }
`

const CREATE_SERVICE = gql`
  mutation createService($shopUuid: GUID!, $service: CreateServiceInput!) {
    createService(shopUuid: $shopUuid, service: $service) {
      serviceUuid
    }
  }
`

const UPDATE_SERVICE = gql`
  mutation updateService($serviceUuid: GUID!, $service: UpdateServiceInput!) {
    updateService(serviceUuid: $serviceUuid, service: $service) {
      service {
        duration
        enabled
        openings {
          beginHour
          dayOfWeek
          endHour
        }
        parallelAppointment
        serviceType
        serviceUuid
      }
      toCancelAppointments {
        appointmentId
        beginDateTime
        customerFullName
        customerPhoneNumber
        endDateTime
        service {
          serviceType
        }
      }
    }
  }
`

export default {
  name: 'ServicesSettings',

  components: {
    Loader,
    CustomServiceCreation,
    AppointmentChangesModal,
    ExceptionalClosureService,
    SelectService,
    ServiceSettings,
    ValidationObserver
  },

  data() {
    return {
      displayAddService: false,
      displayCancelAppointments: false,
      cancelAppointments: [],
      createACustomService: true,
      error: null,
      exceptions: [...this.initServices().map(() => [])],
      isModified: [],
      isExceptionalModified: [],
      loading: false,
      query: {
        services: this.initServices()
      },
      saved: false,
      serviceExceptionalEvents: [],
      tab: 0
    }
  },

  computed: {
    ...mapGetters(['currentShop']),
    services() {
      const res = []
      this.currentShop.services.forEach(service => {
        const index = this.servicesTypes.findIndex(type => service.serviceType === type[1])
        res[index] = service
      })
      return res.filter(el => Boolean(el))
    },
    servicesTypes() {
      return Object.entries(this.$const.serviceType)
    }
  },

  apollo: {
    serviceExceptionalEvents: {
      query: GET_EXCEPTIONAL,
      variables() {
        return { limit: 100, offset: 0, serviceUuid: this.query.services[this.tab].serviceUuid }
      },
      result({ data }) {
        if (!data) return

        const formattedEvents = data.serviceExceptionalEvents.exceptionalEvents
          .map(el => this.formatExceptionalEvent(el))
          .sort((a, b) => {
            const aDate = new Date(a.beginDateTime)
            const bDate = new Date(b.beginDateTime)
            if (aDate > bDate) return -1
            else if (aDate < bDate) return 1
            return 0
          })
        this.$set(this.exceptions, this.tab, formattedEvents)
      },
      skip() {
        return !this.query.services[this.tab] || !this.query.services[this.tab].serviceUuid
      }
    }
  },

  methods: {
    cancelAppointment(appointmentId) {
      this.$apollo.mutate({
        mutation: CANCEL_APPOINTMENT,
        variables: { appointmentId }
      })

      const newExceptions = this.exceptions[this.tab].map(exception => {
        exception.toCancelAppointments = exception.toCancelAppointments.filter(
          appointment => appointment.appointmentId !== appointmentId
        )
        return exception
      })
      const cancelAppointmentIndex = this.cancelAppointments.findIndex(cancel => cancel.appointmentId === appointmentId)

      this.$set(this.exceptions, this.tab, newExceptions)
      this.cancelAppointments.splice(cancelAppointmentIndex, 1)
    },
    changeTab(tab) {
      const prevTab = this.tab
      this.displayAddService = false

      if (this.isModified[this.tab]) {
        this.$store.commit('SET_CONFIRM_MODAL', {
          confirm: () => {
            this.updateService(prevTab)
          },
          display: true,
          title: this.$t('servicesSettings.confirmToSaveModal')
        })
      }

      if (this.isExceptionalModified[this.tab]) {
        this.$store.commit('SET_CONFIRM_MODAL', {
          confirm: () => {
            this.createExceptionalEvents(prevTab)
          },
          display: true,
          title: this.$t('servicesSettings.confirmToSaveModal')
        })
      }

      this.tab = tab
    },
    clickAddService() {
      this.changeTab(this.tab)
      this.displayAddService = true
    },
    async createExceptionalEvents(index) {
      const currentIndex = typeof index === 'number' ? index : this.tab
      const newExceptional = this.exceptions[currentIndex]
        .filter(el => Boolean(el.uuid) && Boolean(el.beginDateTime) && Boolean(el.endDateTime))
        .map(el => this.formatExceptionalEventToMutation(el))

      const { data } = await this.$apollo.mutate({
        mutation: CREATE_SERVICE_EXCEPTIONAL_EVENTS,
        variables: {
          serviceUuid: this.query.services[currentIndex].serviceUuid,
          serviceExceptionalEvents: newExceptional
        }
      })

      const editEvents = this.exceptions[currentIndex].filter(el => Boolean(el.uuid))
      this.isExceptionalModified[currentIndex] = false
      this.exceptions[currentIndex].splice(0, editEvents.length)
      this.$set(this.exceptions, currentIndex, [
        ...data.createServiceExceptionalEvents.map(this.formatExceptionalEvent),
        ...this.exceptions[currentIndex]
      ])
      const cancelAppointments = data.createServiceExceptionalEvents
        .map(event => event.toCancelAppointments)
        .flat()
        .map(appointment => this.formatCancelAppointment(appointment))

      if (cancelAppointments.length > 0) {
        this.cancelAppointments = cancelAppointments
        this.displayCancelAppointments = true
      }
    },
    disableService(value) {
      this.$set(this.isModified, this.tab, true)
      this.query.services[this.tab].enabled = !value
      this.query.services[this.tab].openings = []
    },
    formatCancelAppointment(appointment) {
      return {
        appointmentId: appointment.appointmentId,
        endDateTime: new Date(appointment.endDateTime),
        customerFullName: appointment.customerFullName,
        customerPhoneNumber: appointment.customerPhoneNumber,
        beginDateTime: new Date(appointment.beginDateTime),
        service: { serviceType: appointment.service.serviceType }
      }
    },
    formatExceptionalEvent(event) {
      return {
        beginDateTime: new Date(event.beginDateTime),
        endDateTime: new Date(event.endDateTime),
        eventType: event.eventType,
        exceptionalEventUuid: event.exceptionalEventUuid,
        toCancelAppointments: event.toCancelAppointments,
        type: 'date'
      }
    },
    formatExceptionalEventToMutation(event) {
      return {
        beginDateTime: setToBeginDay(event.beginDateTime),
        endDateTime: setToBeginDay(addDays(event.endDateTime, 1)),
        eventType: event.eventType
      }
    },
    formatService(service, { enabled = false } = { enabled: false }) {
      const currentShop = this.$store.state.shops.find(shop => shop.shopUuid === this.$store.state.currentShopUuid)
      let openings

      if (service.openings.length > 0) {
        openings = service.openings.map(({ beginHour, dayOfWeek, endHour }) => ({
          beginHour,
          dayOfWeek,
          endHour
        }))
      } else {
        openings = currentShop.openings.map(({ beginHour, dayOfWeek, endHour }) => ({
          beginHour,
          dayOfWeek,
          endHour
        }))
      }
      return {
        color: service.serviceType != 'custom' ? null : service.color,
        duration: (service.duration && convertMinutesToHours(service.duration)) || 0.25,
        enabled: enabled || service.enabled,
        icon:
          service.serviceType != 'custom'
            ? this.$const.serviceTypeValues.find(el => el.value === service.serviceType).icon
            : service.icon,
        name: service.name,
        openings,
        parallelAppointment: service.parallelAppointment,
        price: service.price,
        serviceType: service.serviceType,
        serviceUuid: service.serviceUuid
      }
    },
    formatServiceToCreateMutation(service) {
      let mutation

      service.serviceType != 'custom'
        ? (mutation = {
            shopUuid: this.currentShop.shopUuid,
            service: {
              duration: (service.duration && convertHoursToMinutes(service.duration)) || 15,
              openings: service.openings,
              parallelAppointment: service.parallelAppointment,
              serviceType: service.serviceType
            }
          })
        : (mutation = {
            shopUuid: this.currentShop.shopUuid,
            service: {
              color: service.color,
              duration: (service.duration && convertHoursToMinutes(service.duration)) || 15,
              icon: service.icon,
              name: service.name,
              openings: service.openings,
              parallelAppointment: service.parallelAppointment,
              serviceType: service.serviceType
            }
          })

      if (service.price) mutation.service.price = service.price

      return mutation
    },
    formatServiceToUpdateMutation(service) {
      const mutation = {
        serviceUuid: service.serviceUuid,
        service: {
          // ...service,
          duration: (service.duration && convertHoursToMinutes(service.duration)) || 15,
          enabled: service.enabled,
          openings: service.openings,
          parallelAppointment: service.parallelAppointment
        }
      }

      if (service.price) mutation.service.price = service.price

      return mutation
    },
    formatServiceToStore(service) {
      return {
        ...service,
        duration: (service.duration && convertHoursToMinutes(service.duration)) || 15
      }
    },
    initServices() {
      return Object.entries(this.$store.getters.currentShop.services)
        .map(service => {
          const curentService = service[1]

          if (!curentService || !curentService.enabled) return null

          return this.formatService(curentService)
        })
        .filter(curentService => Boolean(curentService))
    },
    openCanceled({ exceptionalEventUuid }) {
      this.cancelAppointments = this.exceptions[this.tab]
        .find(event => event.exceptionalEventUuid === exceptionalEventUuid)
        .toCancelAppointments.map(event => this.formatCancelAppointment(event))
      this.displayCancelAppointments = this.cancelAppointments.length > 0
    },
    removeExceptionalEventLine(index) {
      const event = this.exceptions[this.tab][index]

      if (!event.exceptionalEventUuid) {
        this.exceptions[this.tab].splice(index, 1)
      } else {
        this.$store.commit('SET_CONFIRM_MODAL', {
          confirm: async () => {
            await this.removeExceptionalEvent(event)
            this.exceptions[this.tab].splice(index, 1)
          },
          display: true,
          title: this.$t('servicesSettings.confirmToRemoveExceptionsTitle')
        })
      }
    },
    async removeExceptionalEvent(event) {
      await this.$apollo.mutate({
        mutation: REMOVE_EXCEPTIONAL_EVENT,
        variables: {
          serviceUuid: this.query.services[this.tab].serviceUuid,
          serviceExceptionalEventUuid: event.exceptionalEventUuid
        }
      })
    },
    selectService(serviceObject) {
      let service
      let value

      if (serviceObject.serviceType) {
        value = serviceObject.serviceType
      } else {
        value = serviceObject.value
      }

      if (value != 'custom') {
        service = this.currentShop.services.find(service => service.serviceType === serviceObject.key) || {
          duration: 15,
          enabled: true,
          openings: [],
          parallelAppointment: 1,
          price: null,
          serviceType: serviceObject.key
        }
      } else {
        service = this.currentShop.services.find(service => service.serviceUuid === serviceObject.key) || {
          ...serviceObject,
          duration: 15,
          enabled: true,
          openings: [],
          parallelAppointment: 1,
          price: null
        }
      }

      if (!service.serviceUuid) {
        this.exceptions.push([])
      }

      this.query.services.push(this.formatService(service, { enabled: true }))
      this.tab = this.query.services.length - 1
      this.displayAddService = false
      this.$set(this.isModified, this.query.services.length - 1, true)
    },
    async updateService(index) {
      this.$store.commit('SET_SKIP_MY_USER', true)
      const currentIndex = typeof index === 'number' ? index : this.tab
      const isUpdate = Boolean(this.query.services[currentIndex].serviceUuid)

      const areValidIntervals = this.query.services.every(service =>
        this.validateIntervals(service.openings, service.duration)
      )
      if (!areValidIntervals) {
        this.error = this.$t('servicesSettings.errorMessage.invalidIntervals')
        return
      }

      try {
        this.loading = true
        const { data } = await this.$apollo.mutate({
          mutation: isUpdate ? UPDATE_SERVICE : CREATE_SERVICE,
          variables: isUpdate
            ? this.formatServiceToUpdateMutation(this.query.services[currentIndex])
            : this.formatServiceToCreateMutation(this.query.services[currentIndex])
        })

        if (!isUpdate) {
          this.query.services[currentIndex].serviceUuid = data.createService.serviceUuid
          if (this.query.services[currentIndex].serviceType === 'custom') {
            this.$gtm.trackEvent({
              event: 'custom_service_saved',
              practitioner_id: this.$store.state.myUser.ecpId,
              shopUuid: this.$store.state.currentShopUuid
            })
          }
        }

        this.error = null
        this.$set(this.isModified, currentIndex, false)

        const service = this.formatServiceToStore(this.query.services[currentIndex])
        this.$store.commit('SETUP_SHOP_SERVICE', { service })

        this.loading = false
        this.saved = true
        setTimeout(() => (this.saved = false), 2000)

        if (data.updateService && data.updateService.toCancelAppointments.length > 0) {
          this.cancelAppointments = data.updateService.toCancelAppointments.map(appointment =>
            this.formatCancelAppointment(appointment)
          )
          this.displayCancelAppointments = true
        }
      } catch (error) {
        this.error = error.message
      }
    },
    validateIntervals(openings, duration) {
      return openings.every(({ beginHour, endHour }) => {
        if (typeof beginHour === 'string') {
          return formatHourToFloat(endHour) - formatHourToFloat(beginHour) >= duration
        } else {
          return endHour - beginHour >= duration
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.ServicesSettings-tab {
  font-weight: 600;
}
.ServicesSettings-addService {
  width: 100% !important;
  min-height: 48px;
  height: 100% !important;
  color: grey;
}
</style>
